import type { Address, Applicant, ApplicantEntry, PeriodicValue } from '@/types';
import { periodType } from './const';

const currencyFormatter = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  minimumFractionDigits: 0,
});
const numberFormatter = new Intl.NumberFormat('en-AU', { minimumFractionDigits: 0 });
const dateFormatter = new Intl.DateTimeFormat('en-AU');

export const formatCurrency = (val?: number) => currencyFormatter.format(val ?? 0);
export const formatNumber = (val?: number) => numberFormatter.format(val ?? 0);
export const formatDate = (val?: string) => dateFormatter.format(val ? new Date(val) : new Date());

export const formatAddress = (address?: Address) => {
  if (!address) return '';

  const addressParts = [
    address.addressLine,
    address.addressLine2,
    address.locality,
    [address.state, address.postcode].filter(Boolean).join(' '),
  ];

  return addressParts.filter(Boolean).join(', ');
};

export const formatSimpleAddress = (address?: Partial<Address>): string => {
  if (!address) return '';

  const addressParts = [address.state, address.postcode].filter((part) => part);

  return addressParts.join(' ');
};

export const getFullName = ({ firstName, middleName, lastName }: Applicant | ApplicantEntry) =>
  [firstName, middleName, lastName].filter((name) => name).join(' ');

export const possessive = (noun?: string) => {
  if (noun) {
    const lastLetter = noun.slice(-1);
    return noun + (lastLetter === 's' ? "'" : "'s");
  } else return '';
};

export const formatPeriodicAmt = (val: PeriodicValue) =>
  formatCurrency(val.amount) + ' ' + periodType[val.frequency].label;

export const capitalise = (word?: string) => {
  const s = word?.trim() ?? '';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export const fromSnakeCase = (val: string | undefined | null) => {
  if (!val) return '';
  const sentence = val.replace(/_/g, ' ').trim();
  return (sentence.charAt(0).toUpperCase() + sentence.slice(1)).replace(' cos ', ' COS ');
};

export const snakeCase = (val: string | undefined | null) => {
  if (!val) return '';
  const withoutSpacesAndCommas = val.replace(/[\s,]+/g, '_');
  const withoutHyphens = withoutSpacesAndCommas.replace(/-/g, '_');
  const lowercase = withoutHyphens.toLowerCase();

  return lowercase;
};

export const upperCaseFirstLetter = (inputString: string): string => {
  if (!inputString) return inputString;
  return inputString.charAt(0).toUpperCase() + inputString.slice(1);
};

/**
 * Converts HTML string value to plain text
 * @param {string} htmlString HTML string value
 * @returns {string}
 */
export const stripHtml = (htmlString: string): string => {
  const temporaryElement = document.createElement('DIV');
  temporaryElement.innerHTML = htmlString;
  return temporaryElement.textContent || temporaryElement.innerText || '';
};

export const formatDateTz = (date: string): string => {
  const rawDate = new Date(date);
  const formatter = new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Sydney',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const formattedParts = formatter.formatToParts(rawDate);
  const year = formattedParts.find((p) => p.type === 'year')?.value;
  const month = formattedParts.find((p) => p.type === 'month')?.value;
  const day = formattedParts.find((p) => p.type === 'day')?.value;
  return `${year}-${month}-${day}`;
};
